/** @jsxImportSource @emotion/react */
import { css, Theme, useTheme } from '@emotion/react';
import React, { FC, useState, useLayoutEffect, useRef } from 'react';
import styled from '@emotion/styled/macro';
import { useParams } from 'react-router-dom';

import { Flex, Box } from 'components/styled';
import Text from 'components/styled/Text';
import TitleText from 'components/styled/TitleText';
import { Wrapper, ResponsiveWrapper } from 'components/styled/Wrapper';
import {
	NavButton,
	NavLinkButton,
	NavHrefButton,
} from 'components/styled/Button';
import { HEADER_WRAPPER_ID } from 'components/header';
import ScrollToTopButton from 'components/floatingactions/ScrollToTopButton';

import { CheckmarkIcon } from 'assets';

import { usePageTitle } from 'hooks/seoHooks';

import { useLocalizedMultistring } from 'utils/localization';

import CountupInfo from '../CountupInfo';
import { useScrollToSection } from '../_utils';

import studentsHomeBg from './studentsHomeBg.jpg';
import studentsHomeBlue from './studentsHomeBlue.jpg';
import searchImg from './searchImg.png';
import profileIcon from './profileicon.png';
import mobMarketing from './mobmarketing.png';
import documentsIcon from './documents.png';

//  IMPORT AS SVG ICON INSTEAD
const IconImage = styled.div<{ imgSrc: string; size?: number }>`
	background-image: url(${p => p.imgSrc});
	background-position: center;
	background-size: contain;
	background-repeat: no-repeat;
	height: ${p => p.size ?? 50}px;
`;

// todo: use external fullBackground component
const FullBackground = styled.div<{ imgSrc: string }>`
	background-image: url(${p => p.imgSrc});
	background-size: cover;
	background-position: bottom center;
	background-attachment: fixed;
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: -1;
	min-height: 1500px;
	&::before {
		content: '';
		background: linear-gradient(
			180deg,
			rgba(0, 0, 0, 0.25) 0%,
			rgba(0, 0, 0, 0) 100%
		);
		background-position: center;
		background-size: 100%;
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
	}
`;

const Subtitle: FC<React.ComponentProps<typeof Text>> = ({
	children,
	...props
}) => (
	<Text
		as="h3"
		textAlign="left"
		fontWeight="normal"
		p={0}
		css={css`
			text-transform: uppercase;
		`}
		{...props}
	>
		{children}
	</Text>
);

const useHeaderHeight = () => {
	const [height, setHeight] = useState(0);

	useLayoutEffect(() => {
		function updateHeightSize() {
			setHeight(
				document.getElementById(HEADER_WRAPPER_ID)?.getBoundingClientRect()
					.height ?? 0,
			);
		}

		updateHeightSize();

		window.addEventListener('resize', updateHeightSize);

		return () => window.removeEventListener('resize', updateHeightSize);
	}, []);

	return height;
};

const MuniHomepage: FC = () => {
	const localize = useLocalizedMultistring();
	const { careerCentreUrl } = useTheme();

	// Page title
	usePageTitle(localize({ CZECH: 'Vítejte', ENGLISH: 'Welcome' }));

	const companySectionRef = useRef<HTMLDivElement>(null);
	const studentSectionRef = useRef<HTMLDivElement>(null);

	const { anchor } = useParams<{ anchor: string }>();

	let section: React.RefObject<HTMLDivElement> | null = null;
	switch (anchor) {
		case 'company':
			section = companySectionRef;
			break;
		case 'student':
			section = studentSectionRef;
			break;
	}

	useScrollToSection(section);
	const headerHeight = useHeaderHeight();
	return (
		<Wrapper color="white">
			<Flex
				flexDirection="column"
				justifyContent="center"
				minHeight={`calc(95vh - ${headerHeight}px)`}
				alignItems="center"
			>
				<FullBackground imgSrc={studentsHomeBg} />
				<Flex
					flexDirection="column"
					alignItems="center"
					textAlign="center"
					m="auto"
					css={css`
						text-shadow: 0px 4px 7px rgba(0, 0, 0, 0.5);
					`}
				>
					<Text as="h1" fontSize={['2em', '3em']} whiteSpace="pre-line">
						{localize({
							CZECH: 'Kariérní portál \n Masarykovy univerzity',
							ENGLISH: 'Career portal of \nMasaryk University',
						})}
					</Text>
					<Box maxWidth={430} fontSize="md">
						<Text>
							{localize({
								CZECH:
									'JobCheckIN je oficiální kariérní portál Masarykovy univerzity, který propojuje její studenty a absolventy se zaměstnavateli.',
								ENGLISH:
									'JobCheckIN is the official career portal of Masaryk University, which connects its students and alumni with employers.',
							})}
						</Text>
					</Box>
					<Flex flexDirection={['column', 'row']} my={4}>
						<NavButton
							variant="primary"
							px={4}
							mx={3}
							onClick={() => window.location.replace('/api/jch/auth/student')}
							css={css`
								text-shadow: none;
							`}
						>
							Student
						</NavButton>
						<NavButton
							variant="primary"
							px={4}
							mx={3}
							mt={[3, 0]}
							onClick={() => window.location.replace('/api/jch/auth/student')}
							css={css`
								text-shadow: none;
							`}
						>
							{localize({
								CZECH: 'Absolvent',
								ENGLISH: 'Alumnus',
							})}
						</NavButton>
						<NavLinkButton
							variant="primary"
							px={4}
							mx={3}
							mt={[3, 0]}
							to="/login/company"
							css={css`
								text-shadow: none;
							`}
						>
							{localize({
								CZECH: 'Zaměstnavatel',
								ENGLISH: 'Company',
							})}
						</NavLinkButton>
					</Flex>
				</Flex>

				<Wrapper
					width={1}
					maxHeight={['unset', 156]}
					mt={3}
					justifyContent="center"
					css={css`
						background-color: rgba(0, 0, 0, 0.3);
					`}
				>
					<CountupInfo />
				</Wrapper>
			</Flex>

			<Wrapper
				bg="white"
				color="text"
				css={css`
					overflow: hidden;
				`}
				ref={studentSectionRef}
			>
				<ResponsiveWrapper>
					<Wrapper maxWidth={430} mx="auto" my={3}>
						<TitleText variant="primary">
							{localize({
								CZECH: 'Jsi student?',
								ENGLISH: 'Are you a student?',
							})}
						</TitleText>
					</Wrapper>
					{localize({
						CZECH: '',
						ENGLISH: '',
					})}
					<Flex
						flexDirection={['column', 'column', 'row']}
						alignItems={['center', 'center', 'flex-start']}
						justifyContent="center"
						flexWrap="wrap"
					>
						<Box p={3} px={4} maxWidth={350} minWidth={300}>
							<IconImage imgSrc={profileIcon} size={94} />
							<Subtitle>
								{localize({
									CZECH: 'Představ se',
									ENGLISH: 'Introduce yourself',
								})}
							</Subtitle>
							<Text>
								{localize({
									CZECH:
										'Vytvoř si profil a efektivní online CV v jednom. Představ sebe i svoje kariérní preference, zkušenosti, znalosti a dovednosti.',
									ENGLISH:
										'Create a profile and an effective online CV in one. Introduce yourself and your career preferences, experience, knowledge and skills.',
								})}
							</Text>
						</Box>
						<Box p={3} px={4} maxWidth={350} minWidth={300}>
							<IconImage imgSrc={searchImg} size={94} />
							<Subtitle>
								{localize({
									CZECH: 'Prohlédni si nabídky zaměstnavatelů',
									ENGLISH: "View employer's offers",
								})}
							</Subtitle>
							<Text>
								{localize({
									CZECH:
										'V portálu najdeš pouze studentské a absolventské nabídky! Zaměstnavatel po Tobě nemůže vyžadovat dlouhou praxi, ani nabízet pozice z jiných oborů, než jaké se na MU vyučují.',
									ENGLISH:
										'The portal only lists offers for students and alumni! The employer cannot require long experience from you, nor offer positions from other fields than those taught at MU.',
								})}
							</Text>
						</Box>
						<Box p={3} px={4} maxWidth={350} minWidth={300}>
							<IconImage imgSrc={mobMarketing} size={94} />
							<Subtitle>
								{localize({
									CZECH: 'Nech si doporučit ideální nabídku',
									ENGLISH: 'Get an ideal offer',
								})}
							</Subtitle>
							<Text>
								{localize({
									CZECH:
										'JobCheckIN porovnává Tvoje preference a dovednosti s vypsanými inzeráty a automaticky Ti doporučuje ty nabídky a zaměstnavatele, kteří vyhovují tvým preferencím.',
									ENGLISH:
										'JobCheckIN compares your preferences and skills with job offers and automatically recommends offers and employers that suit your preferences.',
								})}
							</Text>
						</Box>
						<Box p={3} px={4} maxWidth={350} minWidth={300}>
							<IconImage imgSrc={documentsIcon} size={94} />
							<Subtitle>
								{localize({
									CZECH: 'Rozvíjej se',
									ENGLISH: 'Progress',
								})}
							</Subtitle>
							<Text>
								{localize({
									CZECH:
										'Portál Tě informuje, které požadavky v nabídkách splňuješ a které ne. Snadno získáš představu, v čem se zdokonalit, aby o Tebe byl na trhu práce větší zájem.',
									ENGLISH:
										'The portal informs you which requirements you meet in the offers and which you do not. You can easily get an idea of ​​how to improve so that you are more intriguing on the labor market.',
								})}
							</Text>
						</Box>
						<Box p={3} px={4} maxWidth={350} minWidth={300}>
							<IconImage imgSrc={profileIcon} size={94} />
							<Subtitle>
								{localize({
									CZECH: 'Šetři svůj čas',
									ENGLISH: 'Save your time',
								})}
							</Subtitle>
							<Text>
								{localize({
									CZECH:
										'Na nabídku můžeš reagovat jediným stisknutím tlačítka. Navíc, pokud Tvůj profil zaujme zaměstnavatele, mohou Tě sami aktivně oslovit a nabídnout práci.',
									ENGLISH:
										'You can respond to the menu with a single press of a button. In addition, if your profile is of interest to employers, they can actively contact you and offer you a job.',
								})}
							</Text>
						</Box>
					</Flex>
					<Flex alignItems="center" justifyContent="center" m={0} p={3}>
						<NavButton
							variant="primary"
							mt={[2, 4]}
							px={4}
							onClick={() => window.location.replace('/api/jch/auth/student')}
						>
							{localize({
								CZECH: 'Zaregistrovat se',
								ENGLISH: 'Register',
							})}
						</NavButton>
					</Flex>
				</ResponsiveWrapper>
				<Wrapper
					css={(theme: Theme) => css`
						background-image: url(${studentsHomeBlue});
						background-size: cover;
						background-position: top center;
						background-attachment: fixed;
						position: relative;
						color: #fff;
						&::before {
							content: '';
							background-color: ${theme.colors.primary};
							opacity: 0.7;
							position: absolute;
							top: 0;
							bottom: 0;
							left: 0;
							right: 0;
						}
					`}
					ref={companySectionRef}
				>
					<ResponsiveWrapper pt={4}>
						<TitleText color="white">
							{localize({
								CZECH: 'Jste zaměstnavatel?',
								ENGLISH: 'For employers',
							})}
						</TitleText>
						<Flex justifyContent="center">
							<Text maxWidth={450} minWidth={300} textAlign="center">
								{localize({
									CZECH:
										'Chcete efektivně nabírat mladé talenty z řad studentů a absolventů Masarykovy univerzity a budovat svůj brand zaměstnavatele na univerzitní půdě? Vytvořte si v portále JobCheckIN svůj profil, skrze který najdete kandidáty podle Vašich aktuálních náborových potřeb!',
									ENGLISH:
										'Do you want to effectively recruit young talents among students and alumni of Masaryk University and build your employer brand on university premises? Create your profile on the JobCheckIN portal, through which you will find candidates according to your current recruitment needs!',
								})}
							</Text>
						</Flex>
						<Flex
							flexDirection={['column', 'column', 'row']}
							alignItems="center"
							justifyContent="center"
						>
							<Box p={3} maxWidth={350} minWidth={270}>
								<Flex alignItems="center">
									<CheckmarkIcon width={20} color="white" />
									<Subtitle pl={2}>
										{localize({
											CZECH: 'Doporučování ideálních kandidátů',
											ENGLISH: 'Recommendation of ideal candidates',
										})}
									</Subtitle>
								</Flex>
								<Text>
									{localize({
										CZECH:
											'JobCheckIN v reálném čase porovnává Vaše požadavky v pracovních pozicích s uživatelskými profily a doporučuje Vám kandidáty, kteří odpovídají Vašim kritériím, i ty, kteří mají potenciál. Tyto pozice pak naopak také těmto vhodným kandidátům doporučuje.',
										ENGLISH:
											'JobCheckIN compares your job requirements with user profiles in real time and recommends candidates who meet your criteria, as well as those who have potential. It also recommends these positions to these suitable candidates vice versa.',
									})}
								</Text>
							</Box>
							<Box order={[-1, -1, 0]} maxWidth={350} minWidth={270}>
								<Box p={3}>
									<Flex alignItems="center">
										<CheckmarkIcon width={20} color="white" />
										<Subtitle pl={2}>
											{localize({
												CZECH: 'Prezentace zaměstnavatele',
												ENGLISH: 'Employer presentation',
											})}
										</Subtitle>
									</Flex>
									<Text>
										{localize({
											CZECH:
												'Budujte svůj brand zaměstnavatele díky vlastnímu profilu, skrze který budete prezentovat firemní kulturu, popíšete přijímací proces či definujete své požadavky na uchazeče.',
											ENGLISH:
												'Build your employer brand thanks to your own profile, through which you will present the company culture, describe the recruitment process or define your requirements for candidates.',
										})}
									</Text>
								</Box>
								<Box p={3}>
									<Flex alignItems="center">
										<CheckmarkIcon width={20} color="white" />
										<Subtitle pl={2}>
											{localize({
												CZECH: 'Cílená inzerce',
												ENGLISH: 'Targeted advertising',
											})}
										</Subtitle>
									</Flex>
									<Text>
										{localize({
											CZECH:
												'Inzerujte Vaše aktuálně obsazované pracovní pozice, stáže, praxe či návrhy témat závěrečných prací, a to cíleně na relevantní kandidáty.',
											ENGLISH:
												'Advertise your current job offers, traineeships, internships, or proposals for final thesis topics and target them at relevant candidates.',
										})}
									</Text>
								</Box>
							</Box>
							<Box p={3} maxWidth={350} minWidth={270}>
								<Flex alignItems="center">
									<CheckmarkIcon width={20} color="white" />
									<Subtitle pl={2}>
										{localize({
											CZECH: 'Vyhledávejte uchazeče v databázi',
											ENGLISH: 'Search for candidates in the database',
										})}
									</Subtitle>
								</Flex>
								<Text>
									{localize({
										CZECH:
											'Vstupujte aktivně do databáze registrovaných studentů a absolventů a díky široké filtraci oslovte přesně ty, kteří splňují Vaše požadavky.',
										ENGLISH:
											'Actively enter the database of registered students and alumni and, thanks to extensive filtering, address exactly those who meet your requirements.',
									})}
								</Text>
							</Box>
						</Flex>
						<Flex alignItems="center" justifyContent="center" m={0} p={3}>
							<NavLinkButton
								color="white"
								variant="outlined"
								mt={[2, 4]}
								px={4}
								to="/registration/company"
								css={css`
									border: 1px solid white;
								`}
							>
								{localize({
									CZECH: 'Zaregistrovat se',
									ENGLISH: 'Register',
								})}
							</NavLinkButton>
						</Flex>
					</ResponsiveWrapper>
				</Wrapper>
				<Wrapper mx="auto" my={4} textAlign="center">
					<TitleText m={0} variant="primary">
						{localize({
							CZECH: 'O Kariérním centru MU',
							ENGLISH: 'About Career Centre of MU',
						})}
					</TitleText>
					<Text maxWidth={400} p={3} whiteSpace="pre-line">
						{localize({
							CZECH:
								'Jsme Kariérní centrum MU, které pomáhá studentům a čerstvým absolventům Masarykovy univerzity s kariérním rozvojem a usnadňuje jim kontakt se zaměstnavateli.',
							ENGLISH:
								'We are the Career Centre of MU, which helps students and recent alumni of Masaryk University with career development and facilitates their contact with employers.',
						})}
					</Text>
					<Text maxWidth={400} whiteSpace="pre-line">
						{localize({
							CZECH:
								'Zaměstnavatelům umožňujeme poznat studenty a absolventy MUNI a podílet se různými způsoby na podpoře jejich kariéry.',
							ENGLISH:
								'We enable employers to get to know MUNI students and alumni and to take part in supporting their career development.',
						})}
					</Text>
					<Flex alignItems="center" justifyContent="center" m={0} p={3}>
						<NavHrefButton
							href={careerCentreUrl}
							target="_blank"
							variant="primary"
							mx={2}
							mt={[3, 0]}
						>
							{localize({
								CZECH: 'Více informací zde',
								ENGLISH: 'More information here',
							})}
						</NavHrefButton>
					</Flex>
				</Wrapper>
			</Wrapper>

			<ScrollToTopButton />
		</Wrapper>
	);
};

//MuniHomepage.displayName = 'MuniHomepage';

export default MuniHomepage;
